import React from "react";
import ReactDOM from "react-dom";
import { useDialogA11y } from "../Backdrop/Backdrop";
import "./GuideModal.scss";

// Shared modal for "View Sample" / "View Guideline" content. Rendered through a
// portal on document.body so it never takes part in the page's flex/grid
// layout: opening it cannot move or resize the page, sidebar or wrapper.
// Closes on the close button, a backdrop click, or Esc (via useDialogA11y).
export interface GuideModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  titleId: string;
  closeLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

const GuideModal: React.FC<GuideModalProps> = ({
  open,
  onClose,
  title,
  titleId,
  closeLabel = "Close",
  className,
  children,
}) => {
  const dialogRef = useDialogA11y(open, onClose);

  if (!open || typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div
      className="guideModal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={"guideModal__dialog" + (className ? " " + className : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="guideModal__header">
          <h2 className="guideModal__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="guideModal__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <span className="edel-icon-close" aria-hidden="true" />
          </button>
        </div>
        <div className="guideModal__body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export interface GuideGridItem {
  src: string;
  alt: string;
  caption: React.ReactNode;
  valid?: boolean; // true = correct example, false = what to avoid
}

// 2 x 2 example grid with captions (single column on small screens).
export const GuideGrid: React.FC<{ items: GuideGridItem[] }> = ({ items }) => (
  <ul className="guideGrid">
    {items.map((item, i) => (
      <li key={i} className="guideGrid__item">
        <img className="guideGrid__img" src={item.src} alt={item.alt} />
        <p className={"guideGrid__caption " + (item.valid ? "is-valid" : "is-invalid")}>
          <span className="guideGrid__mark" aria-hidden="true">
            {item.valid ? "✓" : "✕"}
          </span>
          {item.caption}
        </p>
      </li>
    ))}
  </ul>
);

export default GuideModal;
